import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useEditor, type Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Heading } from '@tiptap/extension-heading';
import { Paragraph } from '@tiptap/extension-paragraph';
import { liftListItem as pmLiftListItem } from '@tiptap/pm/schema-list';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableHeader } from '@tiptap/extension-table-header';
import { TableCell } from '@tiptap/extension-table-cell';
import { Color } from '@tiptap/extension-color';
import { Image } from '@tiptap/extension-image';
import { EditorState, TextSelection } from '@tiptap/pm/state';
import { MergeToken } from '../MergeToken';
import { FontSize } from './FontSize';
import { sanitizeEmailHtml } from './emailSanitize';
import { sanitizeAndInlineEmailHtml } from '../../../lib/variables/emailHtmlUtils';
import {
  preprocessHtmlForTiptap,
  postprocessHtmlFromTiptap,
  extractPlaceholders,
  stripHtmlToText,
  clampFontSizePx,
  fontSizeSpan,
  colorSpan,
  insertPayloadAtMarker,
  wrapBlockTag,
  formatHtmlForDisplay,
} from './composerCore';

/* ── List-aware block-style commands (PR-11b) ──
 * The listItem schema content is `paragraph block*` — its first child MUST
 * be a paragraph — so stock setBlockType can never convert a list item's
 * paragraph into a heading. TipTap's toggleHeading therefore falls back to
 * clearNodes(), which lifts blocks out of the list heuristically: siblings
 * lose their list and an empty <li> shell is left behind (live-verified).
 * Fix: when the selection touches list items, lift them out of the list
 * properly (liftListItem) inside the same transaction, THEN run the stock
 * toggle on the now-top-level blocks. Likewise, stock setParagraph on a
 * list item ejects it from the list even though its textblock already IS a
 * paragraph — choosing "Paragraph" there must be a no-op. */
function selectionTouchesListItem(state: EditorState): boolean {
  const { doc, selection } = state;
  let found = false;
  doc.nodesBetween(selection.from, selection.to, (node) => {
    if (found) return false;
    if (node.type.name === 'listItem') {
      found = true;
      return false;
    }
    return true;
  });
  return found;
}

function allSelectedTextblocksAreParagraphs(state: EditorState): boolean {
  const { doc, selection, schema } = state;
  let seen = false;
  let all = true;
  doc.nodesBetween(selection.from, selection.to, (node) => {
    if (node.isTextblock) {
      seen = true;
      if (node.type !== schema.nodes.paragraph) all = false;
    }
    return true;
  });
  return seen && all;
}

const ListAwareHeading = Heading.extend({
  addCommands() {
    const parent: any = this.parent?.() ?? {};
    return {
      ...parent,
      toggleHeading:
        (attributes: any) =>
        (props: any) => {
          const { state, tr } = props;
          if (!selectionTouchesListItem(state)) {
            return parent.toggleHeading(attributes)(props);
          }
          // List-touching selection — convert directly on the transaction.
          // Two stock paths are unusable here: toggleNode/setNode fall back
          // to clearNodes() whenever their block range still touches list
          // structure (corrupts output, crashes on node-boundary ranges),
          // and nested commands.* calls evaluate against the chain-START
          // state, not the evolving transaction (a NodeSelection entry
          // makes liftListItem refuse every item). So: lift each selected
          // item with ProseMirror's own liftListItem driven on the live
          // transaction doc, then setNodeMarkup its paragraph into a
          // heading (paragraph content is inline — always valid heading
          // content, no fallback exists on this path).
          if (!(this as any).options.levels.includes(attributes.level)) return false;
          const { schema } = state;
          const headingType = schema.nodes.heading;
          const paragraphType = schema.nodes.paragraph;
          const listItemType = schema.nodes.listItem;

          // Paragraph node position + text range of every listItem the
          // selection touches (entry-state snapshot).
          const items: Array<{ paraPos: number; from: number; to: number }> = [];
          state.doc.nodesBetween(state.selection.from, state.selection.to, (node: any, pos: number) => {
            if (node.type.name === 'listItem' && node.firstChild) {
              items.push({
                paraPos: pos + 1,
                from: pos + 2,
                to: pos + 2 + node.firstChild.content.size,
              });
            }
            return true;
          });
          const origFrom = state.selection.from;
          const origTo = state.selection.to;

          const isInsideListItem = (doc: any, pos: number): boolean => {
            const $p = doc.resolve(pos);
            for (let d = $p.depth; d > 0; d--) {
              if ($p.node(d).type === listItemType) return true;
            }
            return false;
          };

          // Descending: converting later items never shifts earlier ones
          // (positions are re-mapped through the transaction regardless).
          for (const item of [...items].reverse()) {
            const from = tr.mapping.map(item.from);
            const to = tr.mapping.map(item.to);
            const liftState = EditorState.create({
              schema,
              doc: tr.doc,
              selection: TextSelection.create(tr.doc, from, to),
            });
            const lifted = pmLiftListItem(listItemType)(liftState as any, (produced: any) => {
              for (const step of produced.steps) tr.step(step);
            });
            if (!lifted) continue;
            const paraPos = tr.mapping.map(item.paraPos);
            if (
              tr.doc.nodeAt(paraPos)?.type === paragraphType &&
              !isInsideListItem(tr.doc, paraPos + 1)
            ) {
              tr.setNodeMarkup(paraPos, headingType, attributes);
            }
          }
          // Non-list blocks in the original selection (mixed selections,
          // select-all): map the range through this transaction's steps
          // and convert any free-standing paragraph still there. Blocks
          // the loop already converted are headings now — skipped, as are
          // paragraphs still inside a list (their item could not lift).
          const mappedFrom = tr.mapping.map(origFrom);
          const mappedTo = tr.mapping.map(origTo);
          if (mappedTo > mappedFrom) {
            const toConvert: number[] = [];
            tr.doc.nodesBetween(mappedFrom, mappedTo, (node: any, pos: number) => {
              if (node.type === paragraphType && !isInsideListItem(tr.doc, pos + 1)) {
                toConvert.push(pos);
              }
              return true;
            });
            for (const pos of toConvert) {
              tr.setNodeMarkup(pos, headingType, attributes);
            }
          }
          return true;
        },
    };
  },
});

const ListAwareParagraph = Paragraph.extend({
  addCommands() {
    const parent: any = this.parent?.() ?? {};
    return {
      ...parent,
      setParagraph:
        () =>
        (props: any) => {
          // Already-paragraph textblocks: stock setParagraph would eject
          // list items from their list for zero benefit — no-op instead.
          if (allSelectedTextblocksAreParagraphs(props.state)) return true;
          return parent.setParagraph()(props);
        },
    };
  },
});

export type ComposerMode = 'visual' | 'code';

export interface ComposerTemplate {
  /** Display name for the "template inserted" indicator. */
  name?: string;
  /** Optional subject — forwarded to the parent via onTemplateSubject. */
  subject?: string;
  /**
   * Document templates pre-selected on the source email template —
   * forwarded to the parent via onTemplateDocumentTemplates whenever the
   * field is present (an explicit [] clears a previous template's
   * selection: the inserted template's selection replaces it).
   */
  documentTemplateIds?: string[];
  htmlBody: string;
}

export interface CodeOps {
  bold: () => void;
  italic: () => void;
  underline: () => void;
  heading: (level: 0 | 1 | 2 | 3) => void;
  alignLeft: () => void;
  alignCenter: () => void;
  alignRight: () => void;
  bulletList: () => void;
  orderedList: () => void;
  link: (url: string) => void;
  removeLink: () => void;
  undo: () => void;
  redo: () => void;
  clearFormatting: () => void;
}

export interface ComposerApi {
  /** Canonical HTML — the SINGLE source of truth for the composer body. */
  bodyHtml: string;
  isEmpty: boolean;
  /** {{placeholders}} currently present in the body. */
  placeholders: string[];
  mode: ComposerMode;
  setMode: (m: ComposerMode) => void;
  editor: Editor | null;
  disabled: boolean;
  /** Programmatic set — flows state → sanitized DOM write in visual mode. */
  setBodyHtml: (html: string) => void;
  /**
   * Raw state set WITHOUT sanitizing — used only by the HTML/code textarea
   * while typing (sanitizing per keystroke would eat half-typed tags).
   * Sanitization still runs on mode toggle, template insert and send.
   */
  setRawBodyHtml: (html: string) => void;
  reset: (html?: string) => void;
  /** Sanitized, email-client-compatible HTML for sending / scheduling. */
  getSendHtml: () => string;
  /** Cursor-aware template insertion — identical behaviour in both modes. */
  insertTemplate: (t: ComposerTemplate) => void;
  /** Cursor-aware plain-text / {{merge field}} insertion — both modes. */
  insertTextAtCursor: (text: string) => void;
  /** Cursor-aware raw-HTML insertion (images, tables) — both modes. */
  insertHtmlAtCursor: (html: string) => void;
  /** Mode-aware font size (1–50px inline style, email-client-safe). */
  applyFontSize: (px: number) => void;
  /** Mode-aware text colour (inline style, email-client-safe). */
  applyColor: (color: string) => void;
  /** Mode-aware link insertion around the current selection. */
  applyLink: (url: string) => void;
  /** Mode-aware link removal (unwraps the anchor, keeps the text). */
  removeLink: () => void;
  currentFontSize: number | null;
  currentColor: string | null;
  templateInserted: { name: string; at: number } | null;
  dismissTemplateInserted: () => void;
  codeTextareaRef: React.RefObject<HTMLTextAreaElement | null>;
  codeOps: CodeOps;
}

export interface UseComposerBodyOptions {
  initialHtml?: string;
  disabled?: boolean;
  /** Called when an inserted template carries a subject. */
  onTemplateSubject?: (subject: string) => void;
  /** Called when an inserted template carries documentTemplateIds (see ComposerTemplate). */
  onTemplateDocumentTemplates?: (documentTemplateIds: string[]) => void;
}

/* ── State-first visual insertion ───────────────────────────────
 * To keep the HTML string the single source of truth (and to fix template
 * insertion failing in WYSIWYG mode), visual-mode insertions do NOT rely on
 * tiptap's insertContent for the payload. Instead:
 *   1. drop a unique text marker at the cursor via tiptap,
 *   2. read the DOM back and splice the sanitized payload at the marker
 *      IN THE HTML STRING (state now owns the template),
 *   3. write the sanitized state back to the DOM,
 *   4. restore the cursor after the payload and remove the marker.
 * Typing and toolbar formatting still go through tiptap natively, but every
 * keystroke/transaction reports back to state synchronously via onUpdate,
 * so state stays authoritative for send / toggle / preview.
 */
function placeCursorAtMarker(ed: Editor, marker: string): void {
  let found: number | null = null;
  ed.state.doc.descendants((node, pos) => {
    if (found !== null || !node.isText || !node.text) return;
    const idx = node.text.indexOf(marker);
    if (idx >= 0) found = pos + idx;
  });
  if (found === null) return;
  const from = found;
  const to = from + marker.length;
  let tr = ed.state.tr.delete(from, to);
  tr = tr.setSelection(TextSelection.create(tr.doc, from));
  ed.view.dispatch(tr);
  ed.view.focus();
}

export function useComposerBody(options: UseComposerBodyOptions = {}): ComposerApi {
  const { initialHtml = '', disabled = false, onTemplateSubject, onTemplateDocumentTemplates } = options;
  const onTemplateSubjectRef = useRef(onTemplateSubject);
  onTemplateSubjectRef.current = onTemplateSubject;
  const onTemplateDocumentTemplatesRef = useRef(onTemplateDocumentTemplates);
  onTemplateDocumentTemplatesRef.current = onTemplateDocumentTemplates;

  const [bodyHtml, setBodyHtmlState] = useState(() => sanitizeEmailHtml(initialHtml));
  const [mode, setModeState] = useState<ComposerMode>('visual');
  const [templateInserted, setTemplateInserted] = useState<{ name: string; at: number } | null>(null);

  const codeTextareaRef = useRef<HTMLTextAreaElement | null>(null);
  const modeRef = useRef<ComposerMode>(mode);
  modeRef.current = mode;
  const bodyHtmlRef = useRef(bodyHtml);
  bodyHtmlRef.current = bodyHtml;
  const editorRef = useRef<Editor | null>(null);

  /* ── tiptap editor: the WYSIWYG DOM is a render target only ── */
  const editor = useEditor(
    {
      extensions: [
        StarterKit.configure({
          heading: false,
          paragraph: false,
          underline: false,
          link: { openOnClick: false },
        }),
        // List-aware replacements for the StarterKit nodes (PR-11b) —
        // identical schema/names, commands fixed at the editor level so
        // every caller (toolbar, shortcuts, tests) gets the safe behavior.
        ListAwareParagraph,
        ListAwareHeading.configure({ levels: [1, 2, 3] }),
        Underline,
        TextAlign.configure({ types: ['heading', 'paragraph', 'tableCell', 'tableHeader'] }),
        Table.configure({ resizable: true }),
        TableRow,
        TableHeader,
        TableCell,
        // Images (PR-11 §C): without the Image node, ProseMirror silently
        // DROPS <img> from pasted/restored HTML in visual mode. Responsive
        // by default; the send pipeline re-inlines the same rules.
        Image.configure({
          inline: false,
          allowBase64: false,
          HTMLAttributes: { style: 'max-width: 100%; height: auto;' },
        }),
        FontSize,
        Color,
        MergeToken,
      ],
      content: preprocessHtmlForTiptap(sanitizeEmailHtml(initialHtml)),
      editable: !disabled,
      onUpdate: ({ editor: ed }) => {
        const clean = postprocessHtmlFromTiptap(sanitizeEmailHtml(ed.getHTML()));
        setBodyHtmlState((prev) => (prev === clean ? prev : clean));
      },
    },
    [],
  );

  useEffect(() => {
    editorRef.current = editor;
  }, [editor]);

  useEffect(() => {
    if (editor) editor.setEditable(!disabled);
  }, [editor, disabled]);

  /** Write canonical HTML state → sanitized tiptap DOM (visual mode only). */
  const writeBodyToEditor = useCallback((html: string) => {
    const ed = editorRef.current;
    if (!ed || modeRef.current !== 'visual') return;
    const canonicalNow = postprocessHtmlFromTiptap(sanitizeEmailHtml(ed.getHTML()));
    if (canonicalNow !== html) {
      ed.commands.setContent(preprocessHtmlForTiptap(html));
    }
  }, []);

  const setBodyHtml = useCallback(
    (html: string) => {
      const clean = sanitizeEmailHtml(html || '');
      setBodyHtmlState(clean);
      writeBodyToEditor(clean);
    },
    [writeBodyToEditor],
  );

  const reset = useCallback(
    (html = '') => {
      const clean = sanitizeEmailHtml(html);
      setBodyHtmlState(clean);
      setTemplateInserted(null);
      writeBodyToEditor(clean);
    },
    [writeBodyToEditor],
  );

  /** Raw set for the code textarea — no sanitization while typing. */
  const setRawBodyHtml = useCallback((html: string) => {
    setBodyHtmlState(html || '');
  }, []);

  /* ── Mode toggle — lossless both directions (PR-11 §D) ──
   * The EditorContent surface now stays MOUNTED (CSS-hidden) while code
   * mode is open, so the tiptap doc, undo history and selection are
   * never destroyed by a toggle. On the way into code mode we remember
   * the visual selection and pretty-print the source for display
   * (whitespace-only formatting at block boundaries — see
   * formatHtmlForDisplay); on the way back we restore the caret where
   * the doc still accommodates it. */
  const savedSelectionRef = useRef<{ from: number; to: number } | null>(null);
  /* PR-11b: the exact code string shown when entering code mode. If it is
   * unchanged on return, the visual doc was never replaced — skipping the
   * setContent keeps the undo history clean (an unconditional setContent
   * records a junk full-doc history event that silently eats the user's
   * first Ctrl+Z after a no-edit round trip). */
  const codeBaselineRef = useRef<string | null>(null);
  const setMode = useCallback(
    (m: ComposerMode) => {
      if (m === modeRef.current) return;
      const ed = editorRef.current;
      if (m === 'code') {
        // Flush the visual DOM into canonical state before showing source.
        if (ed) {
          const { from, to } = ed.state.selection;
          savedSelectionRef.current = { from, to };
          const clean = postprocessHtmlFromTiptap(sanitizeEmailHtml(ed.getHTML()));
          const formatted = formatHtmlForDisplay(clean);
          codeBaselineRef.current = formatted;
          setBodyHtmlState(formatted);
        }
        setModeState('code');
      } else {
        // Sanitize the edited source, store it, write sanitized DOM.
        const untouched =
          codeBaselineRef.current !== null && bodyHtmlRef.current === codeBaselineRef.current;
        codeBaselineRef.current = null;
        const clean = sanitizeEmailHtml(bodyHtmlRef.current);
        setBodyHtmlState(clean);
        if (ed && untouched) {
          // Code untouched: the mounted editor still holds the exact doc
          // and history from before the toggle — only refocus.
          ed.commands.focus();
        } else if (ed) {
          ed.commands.setContent(preprocessHtmlForTiptap(clean));
          // Restore the caret/selection saved when leaving visual mode.
          const saved = savedSelectionRef.current;
          if (saved) {
            const size = ed.state.doc.content.size;
            const from = Math.min(saved.from, size);
            const to = Math.min(saved.to, size);
            try {
              ed.commands.setTextSelection({ from, to });
            } catch {
              /* selection not representable in the new doc — leave as-is */
            }
          }
          ed.commands.focus();
        }
        setModeState('visual');
      }
    },
    [],
  );

  /* ── Visual-mode cursor-aware insertion (state-first) ── */
  const visualInsertAtCursor = useCallback((payloadHtml: string): boolean => {
    const ed = editorRef.current;
    if (!ed || modeRef.current !== 'visual') return false;
    const marker = `[[__CURSOR_${Math.random().toString(36).slice(2, 10)}__]]`;
    ed.chain().focus().insertContent(marker).run();
    const withMarker = postprocessHtmlFromTiptap(sanitizeEmailHtml(ed.getHTML()));
    const { html: final, markerFound } = insertPayloadAtMarker(withMarker, marker, payloadHtml);
    // STATE OWNS THE PAYLOAD — then write sanitized DOM from state.
    setBodyHtmlState(final);
    ed.commands.setContent(preprocessHtmlForTiptap(final));
    if (markerFound) placeCursorAtMarker(ed, marker);
    return true;
  }, []);

  /* ── Code-mode cursor-aware insertion (pure string splice; state-first) ── */
  const codeInsertAtCursor = useCallback((payloadHtml: string) => {
    const ta = codeTextareaRef.current;
    const current = bodyHtmlRef.current;
    if (ta) {
      const start = ta.selectionStart ?? current.length;
      const end = ta.selectionEnd ?? current.length;
      const next = current.slice(0, start) + payloadHtml + current.slice(end);
      setBodyHtmlState(next);
      const cursor = start + payloadHtml.length;
      requestAnimationFrame(() => {
        ta.focus();
        try {
          ta.selectionStart = ta.selectionEnd = cursor;
        } catch {
          /* noop */
        }
      });
    } else {
      setBodyHtmlState(current + payloadHtml);
    }
  }, []);

  const insertTextAtCursor = useCallback(
    (text: string) => {
      if (!text) return;
      if (modeRef.current === 'visual') {
        // Merge-field tokens stay literal in canonical HTML; the DOM write
        // turns them into pills via preprocessHtmlForTiptap.
        visualInsertAtCursor(text);
      } else {
        codeInsertAtCursor(text);
      }
    },
    [visualInsertAtCursor, codeInsertAtCursor],
  );

  /** Raw-HTML insertion (images, tables) — same state-first path as text. */
  const insertHtmlAtCursor = useCallback(
    (html: string) => {
      if (!html) return;
      if (modeRef.current === 'visual') {
        if (!visualInsertAtCursor(sanitizeEmailHtml(html))) {
          setBodyHtml(`${bodyHtmlRef.current}${sanitizeEmailHtml(html)}`);
        }
      } else {
        codeInsertAtCursor(html);
      }
    },
    [visualInsertAtCursor, codeInsertAtCursor, setBodyHtml],
  );

  const insertTemplate = useCallback(
    (tmpl: ComposerTemplate) => {
      const clean = sanitizeEmailHtml(tmpl.htmlBody || '');
      if (tmpl.subject && onTemplateSubjectRef.current) {
        onTemplateSubjectRef.current(tmpl.subject);
      }
      if (Array.isArray(tmpl.documentTemplateIds) && onTemplateDocumentTemplatesRef.current) {
        onTemplateDocumentTemplatesRef.current(tmpl.documentTemplateIds);
      }
      if (!clean) return;
      if (modeRef.current === 'visual') {
        if (!visualInsertAtCursor(clean)) {
          // Editor view unavailable (e.g. not yet mounted): append through
          // setBodyHtml so the payload lands in state AND is written to the
          // editor DOM when it mounts. A raw state append here would be
          // invisible in the WYSIWYG and silently overwritten by the next
          // keystroke's onUpdate — losing the inserted template.
          setBodyHtml(`${bodyHtmlRef.current}${clean}`);
        }
      } else {
        codeInsertAtCursor(clean);
      }
      // Template is part of editor state immediately (see visual/code paths).
      setTemplateInserted({ name: tmpl.name || 'Template', at: Date.now() });
    },
    [visualInsertAtCursor, codeInsertAtCursor, setBodyHtml],
  );

  const dismissTemplateInserted = useCallback(() => setTemplateInserted(null), []);

  /* ── Code-mode selection helpers (native undo preserved) ── */
  const codeReplaceSelection = useCallback(
    (make: (selected: string) => { text: string; cursorOffset?: number }) => {
      const ta = codeTextareaRef.current;
      if (!ta) return;
      ta.focus();
      const start = ta.selectionStart ?? 0;
      const end = ta.selectionEnd ?? 0;
      const current = bodyHtmlRef.current;
      const selected = current.slice(start, end);
      const { text: replacement, cursorOffset } = make(selected);
      const applyCursor = (base: number, len: number) => {
        requestAnimationFrame(() => {
          try {
            ta.selectionStart = ta.selectionEnd = cursorOffset !== undefined ? base + cursorOffset : base + len;
          } catch {
            /* noop */
          }
        });
      };
      let done = false;
      try {
        if (typeof document.execCommand === 'function') {
          ta.selectionStart = start;
          ta.selectionEnd = end;
          // execCommand('insertText') keeps the native undo stack intact.
          done = document.execCommand('insertText', false, replacement);
        }
      } catch {
        done = false;
      }
      if (done) {
        setBodyHtmlState(ta.value);
        applyCursor(start, replacement.length);
      } else {
        const next = current.slice(0, start) + replacement + current.slice(end);
        setBodyHtmlState(next);
        applyCursor(start, replacement.length);
      }
    },
    [],
  );

  const codeOps: CodeOps = useMemo(
    () => ({
      bold: () =>
        codeReplaceSelection((s) =>
          s ? { text: `<b>${s}</b>` } : { text: '<b></b>', cursorOffset: 3 },
        ),
      italic: () =>
        codeReplaceSelection((s) =>
          s ? { text: `<i>${s}</i>` } : { text: '<i></i>', cursorOffset: 3 },
        ),
      underline: () =>
        codeReplaceSelection((s) =>
          s ? { text: `<u>${s}</u>` } : { text: '<u></u>', cursorOffset: 3 },
        ),
      heading: (level) => codeReplaceSelection((s) => wrapBlockTag(s, level)),
      alignLeft: () =>
        codeReplaceSelection((s) =>
          s ? { text: `<div style="text-align: left">${s}</div>` } : { text: s },
        ),
      alignCenter: () =>
        codeReplaceSelection((s) =>
          s ? { text: `<div style="text-align: center">${s}</div>` } : { text: s },
        ),
      alignRight: () =>
        codeReplaceSelection((s) =>
          s ? { text: `<div style="text-align: right">${s}</div>` } : { text: s },
        ),
      bulletList: () =>
        codeReplaceSelection((s) => {
          if (!s) return { text: s };
          const items = s
            .split('\n')
            .map((l) => l.trim())
            .filter(Boolean)
            .map((l) => `  <li>${l}</li>`)
            .join('\n');
          return { text: `<ul>\n${items}\n</ul>` };
        }),
      orderedList: () =>
        codeReplaceSelection((s) => {
          if (!s) return { text: s };
          const items = s
            .split('\n')
            .map((l) => l.trim())
            .filter(Boolean)
            .map((l) => `  <li>${l}</li>`)
            .join('\n');
          return { text: `<ol>\n${items}\n</ol>` };
        }),
      link: (url: string) =>
        codeReplaceSelection((s) => {
          const safe = url.replace(/"/g, '&quot;');
          return s
            ? { text: `<a href="${safe}">${s}</a>` }
            : { text: `<a href="${safe}"></a>`, cursorOffset: `<a href="${safe}">`.length };
        }),
      removeLink: () =>
        codeReplaceSelection((s) => ({
          text: s.replace(/<a\b[^>]*>([\s\S]*?)<\/a\s*>/gi, '$1'),
        })),
      undo: () => {
        const ta = codeTextareaRef.current;
        if (!ta) return;
        ta.focus();
        try {
          if (typeof document.execCommand === 'function' && document.execCommand('undo')) {
            setBodyHtmlState(ta.value);
          }
        } catch {
          /* noop */
        }
      },
      redo: () => {
        const ta = codeTextareaRef.current;
        if (!ta) return;
        ta.focus();
        try {
          if (typeof document.execCommand === 'function' && document.execCommand('redo')) {
            setBodyHtmlState(ta.value);
          }
        } catch {
          /* noop */
        }
      },
      clearFormatting: () =>
        codeReplaceSelection((s) => ({ text: stripHtmlToText(s) })),
    }),
    [codeReplaceSelection],
  );

  /* ── Mode-aware formatting entry points ── */
  const applyFontSize = useCallback(
    (px: number) => {
      const n = clampFontSizePx(px);
      if (modeRef.current === 'visual') {
        const ed = editorRef.current;
        if (!ed) return;
        ed.chain().focus().setFontSize(`${n}px`).run();
      } else {
        codeReplaceSelection((s) =>
          s ? { text: fontSizeSpan(n, s) } : { text: fontSizeSpan(n, ''), cursorOffset: `<span style="font-size: ${n}px">`.length },
        );
      }
    },
    [codeReplaceSelection],
  );

  const applyColor = useCallback(
    (color: string) => {
      if (!color) return;
      if (modeRef.current === 'visual') {
        const ed = editorRef.current;
        if (!ed) return;
        ed.chain().focus().setColor(color).run();
      } else {
        codeReplaceSelection((s) =>
          s
            ? { text: colorSpan(color, s) }
            : { text: colorSpan(color, ''), cursorOffset: `<span style="color: ${color}">`.length },
        );
      }
    },
    [codeReplaceSelection],
  );

  const applyLink = useCallback(
    (url: string) => {
      if (!url) return;
      if (modeRef.current === 'visual') {
        const ed = editorRef.current;
        if (!ed) return;
        const { from, to } = ed.state.selection;
        if (from === to) {
          ed.chain().focus().insertContent(`<a href="${url}">${url}</a>`).run();
        } else {
          ed.chain().focus().setLink({ href: url }).run();
        }
      } else {
        codeOps.link(url);
      }
    },
    [codeOps],
  );

  const removeLink = useCallback(() => {
    if (modeRef.current === 'visual') {
      const ed = editorRef.current;
      if (!ed) return;
      ed.chain().focus().unsetLink().run();
    } else {
      codeOps.removeLink();
    }
  }, [codeOps]);

  const currentFontSize = useMemo(() => {
    if (mode !== 'visual' || !editor) return null;
    const raw = editor.getAttributes('textStyle').fontSize as string | undefined;
    if (!raw) return null;
    const m = String(raw).match(/([\d.]+)/);
    return m ? Math.round(parseFloat(m[1])) : null;
  }, [mode, editor, bodyHtml]);

  const currentColor = useMemo(() => {
    if (mode !== 'visual' || !editor) return null;
    return (editor.getAttributes('textStyle').color as string | undefined) ?? null;
  }, [mode, editor, bodyHtml]);

  const placeholders = useMemo(() => extractPlaceholders(bodyHtml), [bodyHtml]);
  const isEmpty = useMemo(() => stripHtmlToText(bodyHtml).length === 0, [bodyHtml]);

  const getSendHtml = useCallback(() => {
    // Final gate: shared sanitizer + email-client inlining (img/table fixes).
    return sanitizeAndInlineEmailHtml(sanitizeEmailHtml(bodyHtmlRef.current));
  }, []);

  const api: ComposerApi = {
    bodyHtml,
    isEmpty,
    placeholders,
    mode,
    setMode,
    editor,
    disabled,
    setBodyHtml,
    setRawBodyHtml,
    reset,
    getSendHtml,
    insertTemplate,
    insertTextAtCursor,
    insertHtmlAtCursor,
    applyFontSize,
    applyColor,
    applyLink,
    removeLink,
    currentFontSize,
    currentColor,
    templateInserted,
    dismissTemplateInserted,
    codeTextareaRef,
    codeOps,
  };
  return api;
}
